import { lazy, Suspense, useEffect, useState } from 'react';
import { InfluencerChatDrawerBody } from '@/components/InfluencerChatDrawer';
import { ChannelDrawerRail, type ChannelDrawerSection } from '@/components/ChannelDrawerRail';
import { AddInfluencersDrawerBody } from '@/pages/influencers/components/AddInfluencersDrawerBody';
import type { ChannelDrawerState } from '@/lib/hooks/useChannelDrawer';
import { useChannelEditDialog } from '@/lib/hooks/useChannelEditDialog';
import { useI18nText } from '@/lib/utils/i18nText';
import { PageLoader } from '@/components/Spinner';
import type { Campaign } from '@/pages/influencers/types';

// Lazy: ChannelDrawer is rendered by Header.tsx on every page, so a static
// import of the full UserPanel page here would pull it (and everything it
// imports, e.g. general-posts.tsx) into the main entry bundle.
const PanelUserDrawerBody = lazy(() =>
  import('@/pages/panel/user').then((m) => ({ default: m.PanelUserDrawerBody }))
);

interface ChannelDrawerProps {
  drawer: ChannelDrawerState;
  drawerEntered: boolean;
  onClose: () => void;
  /** Only needed when `drawer.kind` can be `'add-influencers'` — currently just
   *  the influencer dashboard. `'add-influencers'` is a campaign-level action
   *  (not tied to one channel), so it has no tab rail and needs page-local
   *  data the hook has no way to know about. Omit on every other page. */
  addInfluencers?: {
    campaigns: Campaign[];
    isAdmin: boolean;
    organizationId?: string;
    onAdded: (addedUserIds: string[]) => void;
  };
}

/**
 * Renders one of two drawer contents in a fixed right-side panel:
 * - `'channel'`: the merged Channel-detail/Chat-system drawer — a left icon
 *   rail (ChannelDrawerRail) plus both `PanelUserDrawerBody` (Channel detail)
 *   and `InfluencerChatDrawerBody` (Chat system), kept mounted and toggled
 *   with CSS `display` once a tab has been visited (`visited` state below)
 *   rather than unmounted on switch — `InfluencerChatDrawerBody` owns a
 *   separate React root (its own MemoryRouter) that's expensive to tear down
 *   and remount, so flipping tabs would otherwise lose scroll/sub-tab state
 *   every time. Only the first-visited tab mounts eagerly, so opening the
 *   drawer doesn't always pay for both sides' profile fetches up front.
 * - `'add-influencers'`: unrelated to a channel, so it's just its own content
 *   with no rail (see `addInfluencers` prop above).
 */
export function ChannelDrawer({ drawer, drawerEntered, onClose, addInfluencers }: ChannelDrawerProps) {
  const { t } = useI18nText();
  const channelEdit = useChannelEditDialog();
  const [activeSection, setActiveSection] = useState<ChannelDrawerSection>('details');
  const [visited, setVisited] = useState<Set<ChannelDrawerSection>>(() => new Set(['details']));

  // Every open*Drawer() call creates a brand-new `drawer` object (even to
  // re-target the same channel), so resetting on `[drawer]` correctly jumps
  // to the requested tab whether the drawer was closed or already open.
  useEffect(() => {
    if (drawer?.kind !== 'channel') return;
    setActiveSection(drawer.initialSection);
    setVisited(new Set([drawer.initialSection]));
  }, [drawer]);

  if (!drawer) return null;

  const ariaLabel =
    drawer.kind === 'add-influencers'
      ? t('Find & add influencers', '인플루언서 검색 추가')
      : activeSection === 'chat'
        ? t('Chat System', '채팅 시스템')
        : t('Channel details', '채널 상세');

  const selectSection = (section: ChannelDrawerSection) => {
    setActiveSection(section);
    setVisited((prev) => (prev.has(section) ? prev : new Set(prev).add(section)));
  };

  return (
    <>
      <div
        className={`dashboard-panel-user-drawer-overlay${drawerEntered ? ' dashboard-panel-user-drawer-overlay--open' : ''}`}
        role="presentation"
        onClick={onClose}
      />
      <div
        className={`dashboard-panel-user-drawer-panel${drawerEntered ? ' dashboard-panel-user-drawer-panel--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
      >
        {drawer.kind === 'channel' && (
          <div className="channel-drawer-body">
            <ChannelDrawerRail
              active={activeSection}
              onSelect={selectSection}
              onEditChannel={() => void channelEdit.openFor(drawer.userId)}
              editLoading={channelEdit.loading}
            />
            <div className="channel-drawer-pane" style={{ display: activeSection === 'details' ? 'flex' : 'none' }}>
              {visited.has('details') && (
                <Suspense fallback={<PageLoader />}>
                  <PanelUserDrawerBody userId={drawer.userId} initialTab={drawer.initialTab} onClose={onClose} />
                </Suspense>
              )}
            </div>
            <div className="channel-drawer-pane" style={{ display: activeSection === 'chat' ? 'flex' : 'none' }}>
              {visited.has('chat') && (
                <InfluencerChatDrawerBody userId={drawer.userId} campaignId={drawer.campaignId} onClose={onClose} />
              )}
            </div>
          </div>
        )}
        {drawer.kind === 'add-influencers' && addInfluencers && (
          <AddInfluencersDrawerBody
            campaignId={drawer.campaignId}
            isAdmin={addInfluencers.isAdmin}
            campaigns={addInfluencers.campaigns}
            organizationId={addInfluencers.organizationId}
            onClose={onClose}
            onAdded={addInfluencers.onAdded}
          />
        )}
      </div>
      {channelEdit.dialog}
    </>
  );
}
